import { pageOverlayStyle } from '@/lib/pageGeometry';
import { computeTextGlyphBounds } from '@/lib/richText';

/**
 * MaskLayer — Renders white rectangles over the baked-in positions of restored
 * overlays, sitting between the PDF canvas and the interactive overlay layers
 * (AnnotationLayer, EditLayer).
 *
 * Masks three kinds of overlays:
 * 1. Detected blocks (source='detected') that are selected, modified, or deleted.
 * 2. Restored edits (restored=true) WITH bakedBounds — the PDF canvas contains
 *    their flattened pixels (legacy pre-/AP saves, >2MB baked images). An
 *    /AP-carried restored edit has NO bakedBounds and gets no mask: its annot
 *    was stripped from the canvas copy on open, so a mask would white out raw
 *    page pixels (glaring on scanned/colored pages).
 * 3. Restored annotations (restored=true) with bakedBounds — same reason.
 *    Without this, dragging or deleting a restored shape/pen/highlight reveals
 *    the baked pixels underneath as a ghost twin until the next save runs the
 *    whiteout pass.
 *
 * Each mask rect uses the position where the flat-drawn content actually
 * sits — `originalBounds` for detected blocks, `bakedBounds` for restored
 * overlays — NOT the current overlay position. That way dragging an overlay
 * away from its baked spot reveals clean white instead of ghost pixels until
 * the save pipeline makes the erasure permanent.
 *
 * For TEXT edits the rect is sized to the actual glyph extent, not the
 * container box. The container is taller and wider than the rendered text;
 * a container-sized white rectangle paints a slab across the form line
 * sitting beneath the text. Glyph-tight bounds leave that line intact.
 */

// Map our internal font-family names to CSS font stacks for canvas measureText.
// Mirrors CSS_FONT_MAP in EditLayer so what the canvas measures matches what
// pdf-lib will eventually bake (both fall back to Helvetica).
const CSS_FONT_MAP = {
  'Arial': 'Arial, Helvetica, sans-serif',
  'Helvetica': 'Helvetica, Arial, sans-serif',
  'Helvetica-Bold': 'Helvetica, Arial, sans-serif',
  'Helvetica-Oblique': 'Helvetica, Arial, sans-serif',
  'Times-Roman': '"Times New Roman", Times, serif',
  'Times-Bold': '"Times New Roman", Times, serif',
  'Times-Italic': '"Times New Roman", Times, serif',
  'Courier': '"Courier New", Courier, monospace',
  'Lato': 'Lato, Arial, sans-serif',
  'Roboto': 'Roboto, Arial, sans-serif',
  'Inter': 'Inter, Arial, sans-serif',
  'Carlito': 'Carlito, Arial, sans-serif',
  'Roboto Slab': '"Roboto Slab", Georgia, serif',
};

// Cached canvas + 2D context for measuring text widths. One instance for the
// whole module — we just rewrite ctx.font per call.
let _measureCanvas = null;
function getMeasureCtx() {
  if (!_measureCanvas) {
    _measureCanvas = (typeof document !== 'undefined')
      ? document.createElement('canvas')
      : null;
  }
  return _measureCanvas?.getContext('2d') || null;
}

function measureRunWidth(text, segStyles) {
  const ctx = getMeasureCtx();
  if (!ctx) return text.length * (segStyles?.fontSize || 14) * 0.5;
  const family = CSS_FONT_MAP[segStyles?.fontFamily] || segStyles?.fontFamily || 'Helvetica, Arial, sans-serif';
  const weight = segStyles?.bold ? 'bold' : 'normal';
  const style = segStyles?.italic ? 'italic' : 'normal';
  const size = segStyles?.fontSize || 14;
  ctx.font = `${style} ${weight} ${size}px ${family}`;
  return ctx.measureText(text).width;
}

export default function MaskLayer({ geometry, edits, annotations, pageNum, pageWidth, pageHeight, zoom, selectedEditIds }) {
  const pageEdits = (edits || []).filter((e) => {
    if (e.pageNum !== pageNum) return false;
    // Source text is removed from the rendered background by the local
    // content engine; a white mask would damage colored/vector backgrounds.
    if (e.type === 'text') return false;
    // Restored overlays are masked ONLY when their pixels are actually baked
    // into the canvas render (bakedBounds — legacy saves, >2MB images). An
    // /AP-carried overlay was stripped from the canvas copy on open, so there
    // is nothing to hide: a mask there paints white over raw page pixels —
    // invisible on white documents, a glaring white box on scans.
    if (e.restored && e.bakedBounds) return true;
    if (e.source !== 'detected') return false;
    return selectedEditIds?.has(e.id) || e.modified || e.deleted;
  });

  const pageAnnotations = (annotations || []).filter(
    (a) => a.pageNum === pageNum && a.restored && a.bakedBounds,
  );

  if (pageEdits.length === 0 && pageAnnotations.length === 0) return null;

  // For text edits the baked content is just the glyphs — sizing the mask to
  // the container would over-cover. Use glyph-tight bounds anchored at the
  // baked position (so a moved overlay still reveals clean white at the OLD
  // spot, not its current spot).
  const editBounds = (edit) => {
    const containerBox = edit.originalBounds || edit.bakedBounds || {
      x: edit.x, y: edit.y, width: edit.width, height: edit.height,
    };
    if (edit.type !== 'text' || !(edit.content || edit.segments)) return containerBox;
    // Compute glyph-tight bounds using the EDIT's content + styles, anchored
    // at the baked container origin. Use the GLYPH'S y (cap top) — NOT the
    // container top — so the mask sits exactly over the rendered letters
    // (cap → descender), not the empty area above them. Anchoring at the
    // container top with glyph height covers only the upper half of the
    // letters, leaving the lower half of the baked text visible (and any
    // form lines running through the glyph area at baseline level peek out).
    const glyph = computeTextGlyphBounds({ ...edit, x: containerBox.x, y: containerBox.y }, measureRunWidth);
    if (!glyph) return containerBox;
    return {
      x: glyph.x,
      y: glyph.y,
      width: Math.min(glyph.width, containerBox.width),
      height: Math.min(glyph.height, containerBox.height),
    };
  };

  const rects = [
    ...pageEdits.map((e) => ({ key: `edit-${e.id}`, b: editBounds(e) })),
    ...pageAnnotations.map((a) => ({ key: `annot-${a.id}`, b: a.bakedBounds })),
  ];

  return (
    <div
      className="absolute inset-0"
      style={{
        ...pageOverlayStyle(geometry, zoom),
        width: `${pageWidth * zoom}px`,
        height: `${pageHeight * zoom}px`,
        pointerEvents: 'none',
      }}
    >
      {rects.map(({ key, b }) => {
        // Clamp to page bounds so annotation bakedBounds padding (which can
        // extend past the page edge) doesn't paint white onto the gray backdrop.
        const x = Math.max(0, b.x);
        const y = Math.max(0, b.y);
        const w = Math.max(0, Math.min(pageWidth, b.x + b.width) - x);
        const h = Math.max(0, Math.min(pageHeight, b.y + b.height) - y);
        if (w === 0 || h === 0) return null;
        return (
          <div
            key={key}
            style={{
              position: 'absolute',
              left: `${x * zoom}px`,
              top: `${y * zoom}px`,
              width: `${w * zoom}px`,
              height: `${h * zoom}px`,
              backgroundColor: 'white',
            }}
          />
        );
      })}
    </div>
  );
}
